import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { FaChevronLeft } from "react-icons/fa";

import { db } from "./firebase";
import { supabase } from "./pages/supabaseClient";
import { getCached } from "./utils/simpleCache";

import "./TopSparkView.css";

const ONE_HOUR = 60 * 60;

// Same helper as HomeScreen: Supabase storage path -> signed URL
const signedUrlFor = async (imagePath) => {
  if (!imagePath) return null;
  return getCached(`img-${imagePath}`, async () => {
    const { data, error } = await supabase.storage.from("images").createSignedUrl(imagePath, ONE_HOUR);
    if (error) { console.error("URL error:", error); return null; }
    return data.signedUrl;
  });
};

const TopSparkView = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  // Instant open when the card passes the idea through navigation state
  const passed = state?.idea && state.idea.id === id ? state.idea : null;

  const [idea, setIdea] = useState(passed);
  const [loading, setLoading] = useState(!passed);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Direct link / refresh fallback: load from PostedCompetitionIdeas
  useEffect(() => {
    if (passed) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, "PostedCompetitionIdeas", id));
        if (!snap.exists()) {
          if (!cancelled) setNotFound(true);
          return;
        }
        const data = snap.data();

        let authorName = "Anonymous";
        let profileImageUrl = null;
        try {
          const userData = await getCached(`user-${data.uid}`, async () => {
            const userDoc = await getDoc(doc(db, "users", data.uid));
            return userDoc.exists() ? userDoc.data() : null;
          }, 5 * 60 * 1000);
          if (userData) {
            authorName = userData.authorName || userData.name || authorName;
            if (userData.profileImage) profileImageUrl = await signedUrlFor(userData.profileImage);
          }
        } catch (e) { console.warn("User lookup failed", e); }

        const imageUrl = await signedUrlFor(data.imagePath);
        if (!cancelled) setIdea({ id: snap.id, ...data, authorName, profileImageUrl, imageUrl });
      } catch (e) {
        console.error("Error loading top spark:", e);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };

  }, [id]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="tsv-page">
      <div className="tsv-bar">
        <button type="button" className="tsv-back" onClick={goBack} aria-label="Go back">
          <FaChevronLeft />
        </button>
        <span className="tsv-bar-title">Top Spark</span>
      </div>

      <main className="tsv-sheet">
        <header className="tsv-masthead">
          <div className="tsv-masthead-name">The Trescend Times</div>
          <div className="tsv-masthead-meta">
            <span>Top Sparks Edition</span>
            {idea?.rank ? <span>No. {idea.rank}</span> : null}
          </div>
        </header>

        {loading && (
          <div className="tsv-state" aria-live="polite">
            <div className="tsv-sk tsv-sk-title" />
            <div className="tsv-sk tsv-sk-line" />
            <div className="tsv-sk tsv-sk-photo" />
            <div className="tsv-sk tsv-sk-line" />
            <div className="tsv-sk tsv-sk-line short" />
          </div>
        )}

        {!loading && (notFound || !idea) && (
          <div className="tsv-state tsv-empty">
            <h1 className="tsv-headline">Story not found</h1>
            <p className="tsv-body">This spark is no longer on the front page. Head back and pick another one.</p>
            <button type="button" className="tsv-home-btn" onClick={goBack}>Go back</button>
          </div>
        )}

        {!loading && idea && (
          <article>
            <h1 className="tsv-headline">{idea.title || "Untitled Spark"}</h1>

            <div className="tsv-byline">
              <button
                type="button"
                className="tsv-author"
                onClick={() => idea.uid && navigate(`/journey/${idea.uid}`)}
                disabled={!idea.uid}
              >
                {idea.profileImageUrl ? (
                  <img src={idea.profileImageUrl} alt="" className="tsv-avatar" />
                ) : (
                  <span className="tsv-avatar">{idea.authorName?.[0]}</span>
                )}
                <span className="tsv-author-text">
                  By <b>{idea.authorName}</b>
                </span>
              </button>
            </div>

            {idea.tags?.length > 0 && (
              <div className="tsv-tags">
                {idea.tags.map((t, i) => (
                  <span className="tsv-tag" key={`${t}-${i}`}>{t}</span>
                ))}
              </div>
            )}

            {idea.imageUrl && (
              <figure className="tsv-photo">
                <img src={idea.imageUrl} alt={idea.title || "Idea"} className="tsv-photo-img" />
                <figcaption>
                  {idea.title ? `${idea.title}` : "The idea"}
                  {idea.authorName ? `, by ${idea.authorName}` : ""}
                </figcaption>
              </figure>
            )}

            <p className="tsv-body">
              {idea.description || "No description was filed for this spark."}
            </p>

            <div className="tsv-end">End of story</div>
          </article>
        )}
      </main>
    </div>
  );
};

export default TopSparkView;